import React, { useCallback, useState } from "react";
import { Modal } from "../../../components";
import { importAccDocsAPI } from "../../../common/services";
import { useToaster } from "../../../common/contexts/toast";
import { errorMessageOf } from "../../../utils";
import AccFileBrowser from "./acc-file-browser";

const EMPTY_SELECTION = { hubId: "", projectId: "", items: [], folders: [] };

// The import runs as a backend job: ADD queues it, hands the job to
// `onImportQueued(job)` and closes. The caller tracks it (useAccDocsImport) and
// passes `importing` back so a second import can't start meanwhile.
// `valueIds` (optional) tags every imported doc, e.g. the placement values
// `house:<houseId>` / `houseObject:<objectId>` when importing inside a project.
const SelectAccFilesModal = ({ visible, onClose, onImportQueued, importing = false, valueIds }) => {
    const [selection, setSelection] = useState(EMPTY_SELECTION);
    const [queueing, setQueueing] = useState(false);
    const { showToaster } = useToaster();

    const selectedCount = selection.items.length + selection.folders.length;
    const isBusy = queueing || importing;

    const handleClose = useCallback(() => {
        setSelection(EMPTY_SELECTION);
        onClose && onClose();
    }, [onClose]);

    const handleImport = useCallback(async () => {
        if (!selectedCount || isBusy) return;
        setQueueing(true);
        try {
            const res = await importAccDocsAPI({
                hubId: selection.hubId,
                projectId: selection.projectId,
                items: selection.items,
                folders: selection.folders,
                ...(valueIds?.length ? { valueIds } : {}),
            });
            onImportQueued && onImportQueued(res?.data?.job);
            showToaster && showToaster("acc-import", "Importing documents from Forma is started", true);
            handleClose();
        } catch (error) {
            showToaster && showToaster("acc-import-error", errorMessageOf(error, "Failed to import documents from Forma"), false);
        } finally {
            setQueueing(false);
        }
    }, [selection, selectedCount, isBusy, valueIds, onImportQueued, handleClose, showToaster]);

    return (
        <Modal
            visible={visible}
            title="Select documents"
            onClose={handleClose}
            onClickCancel={handleClose}
            onClickConfirm={handleImport}
            cancelButtonName="CANCEL"
            confirmButtonName="ADD"
            // one import at a time: reopening mid-import shows it still running
            confirmButtonDisable={!selectedCount || isBusy}
            confirmButtonLoading={isBusy}
            size="extra-large"
            modalClass="select-doc-acc"
        >
            {visible && <AccFileBrowser onSelectionChange={setSelection} />}
        </Modal>
    );
};

export default SelectAccFilesModal;
